import type { Cents, DepositAccount } from '@fruitful/types';

export enum TransactionDcSign {
    CREDIT = 'CREDIT',
    DEBIT = 'DEBIT',
}

export enum TransactionType {
    ACH = 'ACH',
    CARD = 'CARD',
    CASH = 'CASH',
    CHECK = 'CHECK',
    DISPUTE = 'DISPUTE',
    EXTERNAL_CARD = 'EXTERNAL_CARD',
    INTERNAL_TRANSFER = 'INTERNAL_TRANSFER',
    WIRE = 'WIRE',
    REWARD = 'REWARD',
    FEE = 'FEE',
}

export enum LegacyTransactionStatus {
    PENDING = 'PENDING',
    POSTED = 'POSTED',
    DECLINED = 'DECLINED',
    COMPLETE = 'COMPLETE',
}

export enum AccountTypeFilter {
    ALL = 'all',
    CASH = 'cash',
    CARD = 'card',
}

export interface LegacyTransaction {
    uuid: string;
    created: string;
    postedDate: string;
    description: string;
    subtype: string;
    amount: Cents;
    formattedAmount: string;
    formattedBalanceAfterTransaction: string;
    balanceAfterTransaction?: Cents;
    type: TransactionType;
    dcSign: TransactionDcSign;
    status: LegacyTransactionStatus;
}

export const AMOUNT_SIGN_BY_TYPE: Record<
    TransactionDcSign & Lowercase<TransactionDcSign>,
    string
> = {
    CREDIT: '+',
    DEBIT: '-',
    credit: '+',
    debit: '-',
};

export const TRANSACTION_TYPES_DESCRIPTION_MAP: Record<
    TransactionType,
    string
> = {
    ACH: 'ACH',
    CARD: 'Card',
    CASH: 'Cash',
    CHECK: 'Check',
    DISPUTE: 'Dispute',
    EXTERNAL_CARD: 'External card',
    INTERNAL_TRANSFER: 'Internal transfer',
    WIRE: 'Wire',
    REWARD: 'Reward',
    FEE: 'Fee',
};

export type NormalizedTransaction2Status = 'pending' | 'posted' | 'declined';

export interface Transaction {
    uuid: string;
    type: Lowercase<TransactionType>;
    subtype: string;
    status: NormalizedTransaction2Status;
    transactionTime: string; // Not created time; this is the date of the transaction
    postedDate?: string; // Not populated for pending card transactions
    dcSign: Lowercase<TransactionDcSign>;
    amount: Cents;
    isPayment: boolean; // true if a transaction is being used to pay off
    description: string;
    cardId?: string;
    statementDescription?: string;
    merchantCategory?: string;
    merchantGuid?: string;
    formattedType: string;
    formattedTypeSubtext?: string;
    enhancedMerchantName?: string;
    pairedDepositAccount?: DepositAccount;
    primaryDepositAccount?: DepositAccount;
    holdExpirationDate?: string;
}

export interface TransactionTableRequest {
    bankAccountId: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    postedPageToken?: string;
    type?: AccountTypeFilter;
    uuid?: string;
}

export interface TransactionTableResponse {
    pendingChargeSecuredAccountTransactions: Transaction[];
    pendingDepositoryAccountTransactions: Transaction[];
    postedTransactions: Transaction[]; // A single table containing posted cash and card transactions
    nextPostedPageToken: string | null;
}

export interface PaymentTransactionTableRequest {
    syncteraAccountId: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    postedPageToken?: string;
}

export interface PaymentTransactionsTableResponse {
    pendingPaymentTransactions: Transaction[];
    postedPaymentTransactions: Transaction[];
    nextPostedPageToken: string | null;
}
